import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Activity, Cpu, MemoryStick, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

import { parseUtc } from '../../lib/parseUtc';
/**
 * Recent spike timeline — reads /clusters/{id}/spike-events which is
 * backed by the agent's local_spike_detector pushing one immutable doc
 * per detected spike via /v1/recommendations/{id}/result.
 *
 * Shows the latest 50; the backend caps at 200.
 */
export default function RecentSpikesPanel({ clusterId, workload, limit = 50 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    if (!clusterId) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: String(limit) });
      if (workload?.namespace) q.set('namespace', workload.namespace);
      if (workload?.name) q.set('workload', workload.name);
      const resp = await axios.get(
        `${API}/clusters/${clusterId}/spike-events?${q.toString()}`,
      );
      setEvents(resp.data?.events || []);
    } catch (err) {
      console.error('Failed to fetch spike events:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [clusterId, workload, limit]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const formatTime = (iso) => {
    if (!iso) return '-';
    const d = parseUtc(iso);
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    return d.toLocaleString();
  };

  const kindIcon = (kind) => {
    if (kind === 'memory') return <MemoryStick className="w-3.5 h-3.5" />;
    if (kind === 'both') return <Activity className="w-3.5 h-3.5" />;
    return <Cpu className="w-3.5 h-3.5" />;
  };

  const kindLabel = (kind) => {
    if (kind === 'memory') return 'Memory';
    if (kind === 'both') return 'CPU + Mem';
    return 'CPU';
  };

  const kindBadge = (kind) => {
    if (kind === 'memory') return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30';
    if (kind === 'both') return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30';
    return 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            Recent spikes
            <Badge variant="outline" className="text-xs ml-1">{events.length}</Badge>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchEvents} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500 py-2">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-gray-500 py-4 text-center">
            No spike events detected in the last 30 days.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="flex items-center justify-between gap-3 p-2.5 rounded border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/40"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Badge className={`text-xs flex items-center gap-1 ${kindBadge(ev.kind)}`}>
                    {kindIcon(ev.kind)}
                    {kindLabel(ev.kind)}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                      {ev.namespace}/{ev.workload_name}
                    </div>
                    {ev.container && (
                      <div className="text-xs text-zinc-500 truncate">
                        container: {ev.container}
                        {typeof ev.ratio === 'number' && ev.ratio > 1.0 && (
                          <span className="ml-2 text-orange-600 dark:text-orange-400">
                            {ev.ratio.toFixed(1)}×
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs text-zinc-500 whitespace-nowrap">
                  <div>{formatTime(ev.ts)}</div>
                  {ev.kind === 'cpu' && ev.old_cpu && ev.new_cpu && (
                    <div className="font-mono text-[10px]">{ev.old_cpu} → {ev.new_cpu}</div>
                  )}
                  {ev.kind === 'memory' && ev.old_memory && ev.new_memory && (
                    <div className="font-mono text-[10px]">{ev.old_memory} → {ev.new_memory}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
